import { useState } from 'react'
import { sendFeedback } from '@/lib/sentry'

const COLORS = {
  pageBg: '#1a1310',
  cardBg: '#fef9c3',
  text: '#2a1f18',
  textMuted: '#6b5740',
  textSubtle: '#8b7355',
  buttonPrimary: '#5c4a38',
  buttonPrimaryText: '#f5e6d3',
  border: '#c4a472',
  inputBg: '#fffef0',
}

const cardShadow =
  '0 8px 16px rgba(0,0,0,0.35), 0 4px 6px rgba(0,0,0,0.2), -2px 6px 10px rgba(0,0,0,0.15), 2px 6px 10px rgba(0,0,0,0.15)'

export default function SentryErrorFeedback({ eventId }) {
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  function handleSubmit(e) {
    e.preventDefault()
    if (!message.trim() || submitting) return
    setSubmitting(true)
    try {
      sendFeedback({
        message: message.trim(),
        associatedEventId: eventId,
      })
    } catch {
      // Swallow — even if Sentry fails, we want to confirm to the user.
    }
    setSubmitted(true)
    setSubmitting(false)
  }

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 p-8"
      style={{ backgroundColor: COLORS.pageBg, minHeight: '100vh' }}
    >
      <div
        style={{
          backgroundColor: COLORS.cardBg,
          padding: '32px 28px',
          boxShadow: cardShadow,
          maxWidth: '420px',
          width: '100%',
          transform: 'rotate(-1.5deg)',
        }}
      >
        <div style={{ fontSize: '32px', marginBottom: '8px', lineHeight: 1 }}>:/</div>
        <h2
          style={{
            fontSize: '18px',
            fontWeight: 800,
            color: COLORS.text,
            marginBottom: '8px',
          }}
        >
          Yikes — that wasn&apos;t supposed to happen
        </h2>
        <p
          style={{
            fontSize: '13px',
            color: COLORS.textMuted,
            marginBottom: '16px',
            lineHeight: 1.5,
          }}
        >
          Sorry — we&apos;re in beta and bugs slip through. The error has been
          reported automatically. If you tell us what you were trying to do
          we&apos;ll fix it faster.
        </p>

        {submitted ? (
          <p
            style={{
              fontSize: '13px',
              color: COLORS.text,
              fontWeight: 600,
              marginBottom: '20px',
              lineHeight: 1.5,
            }}
          >
            Thanks. We got it — appreciate you letting us know.
          </p>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What were you trying to do? (Optional but helpful.)"
              rows={4}
              maxLength={1000}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '13px',
                fontFamily: 'inherit',
                color: COLORS.text,
                backgroundColor: COLORS.inputBg,
                border: `1.5px solid ${COLORS.border}`,
                borderRadius: '6px',
                resize: 'vertical',
                marginBottom: '8px',
                lineHeight: 1.5,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={!message.trim() || submitting}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  backgroundColor: COLORS.buttonPrimary,
                  color: COLORS.buttonPrimaryText,
                  fontWeight: 600,
                  fontSize: '12px',
                  border: 'none',
                  cursor: message.trim() && !submitting ? 'pointer' : 'not-allowed',
                  opacity: message.trim() && !submitting ? 1 : 0.5,
                }}
              >
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </form>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              backgroundColor: COLORS.buttonPrimary,
              color: COLORS.buttonPrimaryText,
              fontWeight: 600,
              fontSize: '13px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <a
            href="/"
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: `1.5px solid ${COLORS.border}`,
              color: COLORS.buttonPrimary,
              fontWeight: 600,
              fontSize: '13px',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Go home
          </a>
        </div>

        {eventId && (
          <p
            style={{
              fontSize: '10px',
              color: COLORS.textSubtle,
              marginTop: '14px',
              fontFamily: 'monospace',
              letterSpacing: '0.02em',
            }}
          >
            ref: {eventId}
          </p>
        )}
      </div>
    </div>
  )
}
